import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import AppInput from "@src/components/AppInput";
import TimesIcon from "@assets/img/times-icon.svg";
import { theme } from "@src/theme";
import Spinner from "@src/components/Spinner";
import { PlaylistStatus } from "@src/types/playlist";
import AppSelectDropdown from "@src/components/AppSelectDropdown";
import {
  CloseDrawerButton,
  SubmitDrawerButton,
  TopButtonsGroup,
} from "@src/components/ContentDrawer/DrawerComponents";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import Tooltip from "@src/components/Tooltip";
import { resetFilters } from "@src/redux/slices/playlistSlice";
import DateRangePicker from "@src/components/DateRangePicker";
import GuestDropdown from "@src/components/GuestDropdown/GuestDropdown";
import CustomAppInput from "@src/components/AppInput/AppInput.styles";
import { IGuestCuratorRecord } from "@src/types/curator";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { useMyShows } from "@src/hooks/useMyShows";
import { XIcon } from "lucide-react";

const schema = yup
  .object({
    showId: yup.string().optional(),
    playlistName: yup.string().optional(),
    description: yup.string().optional(),
    lastModified: yup
      .object()
      .shape({
        startDate: yup.date().optional(),
        endDate: yup.date().optional(),
      })
      .optional(),
    dateAired: yup
      .object()
      .shape({
        startDate: yup.date().optional(),
        endDate: yup.date().optional(),
      })
      .optional(),
    durationInMinutes: yup.number().optional(),
    guestId: yup.string().optional(),
    playlistStatus: yup
      .mixed<PlaylistStatus>()
      .oneOf(["draft", "scheduled", "submitted", "aired"])
      .optional(),
  })
  .required();
export type FilterPlaylistsFormValues = {
  showId?: string;
  playlistName?: string;
  description?: string;
  lastModified?: {
    startDate?: Date;
    endDate?: Date;
  };
  dateAired?: {
    startDate?: Date;
    endDate?: Date;
  };
  durationInMinutes?: number;
  guestId?: string;
  playlistStatus?: PlaylistStatus;
};
type FilterPlaylistsFormProps = {
  closeButtonHandler: () => void;
  submitButtonHandler: (data: FilterPlaylistsFormValues) => Promise<void>;
  fetchingPlaylist: boolean;
  isOpen?: boolean;
};
const defaultValues: FilterPlaylistsFormValues = {
  showId: "",
  playlistName: "",
  description: "",
  lastModified: undefined,
  dateAired: undefined,
  durationInMinutes: 0,
  guestId: "",
  playlistStatus: undefined,
};
const FilterPlaylistsForm: React.FC<FilterPlaylistsFormProps> = ({
  fetchingPlaylist,
  closeButtonHandler,
  submitButtonHandler,
  isOpen,
}) => {
  const dispatch = useAppDispatch();
  const filtersApplied = useAppSelector((state) => state.playlists.filters);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      showId: filtersApplied.showId || "",
      playlistName: filtersApplied.playlistName || "",
      description: filtersApplied.description || "",
      lastModified: filtersApplied.lastModified,
      dateAired: filtersApplied.dateAired,
      durationInMinutes: filtersApplied.durationInMinutes || 0,
      guestId: filtersApplied.guestCuratorId || "",
      playlistStatus: filtersApplied.playlistStatus,
    },
  });

  const curator = useAuthCurator();
  const roleType = useRole();
  const { myShows, loadingMyShows } = useMyShows({
    curatorId: curator!.id,
    roleType,
    searchText: "",
  });
  useEffect(() => {
    if (isOpen) {
      const formValues = {
        showId: filtersApplied.showId || "",
        playlistName: filtersApplied.playlistName || "",
        description: filtersApplied.description || "",
        lastModified: filtersApplied.lastModified,
        dateAired: filtersApplied.dateAired,
        durationInMinutes: filtersApplied.durationInMinutes || 0,
        guestId: filtersApplied.guestCuratorId || "",
        playlistStatus: filtersApplied.playlistStatus,
      };
      reset(formValues, { keepDefaultValues: true });
    }
  }, [isOpen, filtersApplied, reset]);

  const hasFiltersApplied = useMemo(() => {
    const hasSearchText =
      filtersApplied.searchText && filtersApplied.searchText.trim() !== "";
    const hasShowId =
      filtersApplied.showId && filtersApplied.showId.trim() !== "";
    const hasPlaylistName =
      filtersApplied.playlistName && filtersApplied.playlistName.trim() !== "";
    const hasDescription =
      filtersApplied.description && filtersApplied.description.trim() !== "";
    const hasLastModified =
      filtersApplied.lastModified?.startDate ||
      filtersApplied.lastModified?.endDate;
    const hasDateAired =
      filtersApplied.dateAired?.startDate || filtersApplied.dateAired?.endDate;
    const hasDuration =
      filtersApplied.durationInMinutes && filtersApplied.durationInMinutes > 0;
    const hasGuestId =
      filtersApplied.guestCuratorId &&
      filtersApplied.guestCuratorId.trim() !== "";
    const hasStatus = filtersApplied.playlistStatus !== undefined;

    return (
      hasSearchText ||
      hasShowId ||
      hasPlaylistName ||
      hasDescription ||
      hasLastModified ||
      hasDateAired ||
      hasDuration ||
      hasGuestId ||
      hasStatus
    );
  }, [filtersApplied]);

  const onSubmit = async (data: FilterPlaylistsFormValues): Promise<void> => {
    if (fetchingPlaylist) return;
    await submitButtonHandler(data as FilterPlaylistsFormValues);
  };

  const resetFiltersCb = () => {
    reset(defaultValues);
    dispatch(resetFilters());
  };
  const onClose = async () => {
    closeButtonHandler();
    await submitButtonHandler(defaultValues as FilterPlaylistsFormValues);
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TopButtonsGroup>
        <CloseDrawerButton
          style={{ minWidth: 30, minHeight: 30 }}
          type="button"
          onClick={onClose}
        >
          <img
            src={TimesIcon}
            alt="X"
            style={{ width: "10px", height: "10px" }}
          />
        </CloseDrawerButton>
        {fetchingPlaylist && <Spinner small />}
        <div style={{ display: "flex", gap: theme.margins.medium }}>
          {(isDirty || hasFiltersApplied) && (
            <Tooltip content={"Clear filters"} position="top">
              <SubmitDrawerButton
                type="button"
                onClick={resetFiltersCb}
                disabled={fetchingPlaylist}
              >
                Reset filters
              </SubmitDrawerButton>
            </Tooltip>
          )}
          <Tooltip content={"Submit filters"} position="top">
            <SubmitDrawerButton disabled={fetchingPlaylist}>
              Save filters
            </SubmitDrawerButton>
          </Tooltip>
        </div>
      </TopButtonsGroup>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          disabled={loadingMyShows}
          name="showId"
          control={control}
          render={({ field }) => (
            <AppSelectDropdown
              options={myShows}
              labelAccessor="title"
              valueAccessor="documentId"
              label={"Show"}
              control={control}
              errors={errors}
              {...field}
              iconRight={
                field.value ? (
                  <Tooltip content="Remove">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        field.onChange("");
                      }}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        pointerEvents: "auto",
                        zIndex: 20,
                        position: "relative",
                      }}
                    >
                      <XIcon size={16} />
                    </span>
                  </Tooltip>
                ) : (
                  <></>
                )
              }
              placeholder="Select show"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="playlistName"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Playlist"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Keyword(s)"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Description"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Keyword(s)"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="lastModified"
          control={control}
          render={({ field }) => (
            <DateRangePicker
              iconRight={
                field.value && (
                  <Tooltip content="Clear">
                    <XIcon
                      color={theme.colors.yellow}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setValue("lastModified", undefined);
                      }}
                    />
                  </Tooltip>
                )
              }
              onDateChange={(date: any) => {
                setValue("lastModified", date);
              }}
              label={"Date"}
              control={control}
              errors={errors}
              {...field}
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="dateAired"
          control={control}
          render={({ field }) => (
            <DateRangePicker
              iconRight={
                field.value && (
                  <Tooltip content="Clear">
                    <XIcon
                      color={theme.colors.yellow}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setValue("dateAired", undefined);
                      }}
                    />
                  </Tooltip>
                )
              }
              onDateChange={(date: any) => {
                setValue("dateAired", date);
              }}
              label={"Date Aired"}
              control={control}
              errors={errors}
              {...field}
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <CustomAppInput
          label={"Duration (minutes)"}
          name="durationInMinutes"
          control={control}
          errors={errors}
          placeholder="Duration"
          type="range"
          displayInput
          min={0}
          max={160}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <CustomAppInput
          label={"Guest"}
          name="guestId"
          control={control}
          errors={errors}
          placeholder="Guest"
          type="string"
        >
          <GuestDropdown
            value={watch("guestId")}
            onGuestSelect={(guestCurator: IGuestCuratorRecord) => {
              setValue("guestId", guestCurator.documentId);
            }}
            onClear={() => setValue("guestId", "")}
          />
        </CustomAppInput>
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="playlistStatus"
          control={control}
          render={({ field }) => (
            <AppSelectDropdown
              label={"Status"}
              control={control}
              errors={errors}
              options={[
                { value: "draft", label: "Draft" },
                { value: "scheduled", label: "Scheduled" },
                { value: "submitted", label: "Submitted" },
                { value: "aired", label: "Aired" },
              ]}
              {...field}
              value={field.value ?? undefined}
              resetValue={() => field.onChange(undefined)}
              placeholder="Select status"
            />
          )}
        />
      </div>
    </form>
  );
};

export default FilterPlaylistsForm;
